/**
 * Pressure Testing write dialogs: Add instrument, Edit specs, Record calibration.
 *
 * PRESSURES GO TO THE API IN THE TENANT'S UNIT, AS TYPED. The API converts to
 * kPa itself (see lib/pressure-testing.ts), so the inputs are labelled with
 * `meta.pressure_unit` and nothing here converts. A dialog whose unit has not
 * arrived does not open — its caller waits on the meta.
 *
 * Every field the API validates is validated by the API: a 400 arrives with
 * its own message, which is shown as sent. The dialogs check only what would
 * otherwise go out as a type the API cannot read (a number field holding text).
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  PtSpecsAttachError,
  useCreatePtInstrument,
  useRecordPtCalibration,
  useUpdatePtSpecs,
} from '../../hooks/usePressureTesting';
import {
  PT_KIND_EQUIPMENT_TYPE,
  PT_KIND_LABEL,
  pressureFromInput,
  pressureInputValue,
} from '../../lib/pressure-testing';
import {
  PT_INSTRUMENT_KINDS,
  type PtInstrument,
  type PtInstrumentKind,
  type PtPressureUnit,
  type PtSpecsPatch,
} from '../../types/pressure-testing';
import { PT_INPUT, PT_SECONDARY, PtDialog, PtField, ptErrorMessage } from './PtBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';

function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

/** A whole number, null when blank, NaN when not a whole number. */
function intFromInput(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : NaN;
}

// ─── Add instrument ──────────────────────────────────────────────────────────

interface InstrumentValues {
  name: string;
  serial: string;
  manufacturer: string;
  model: string;
  kind: PtInstrumentKind;
  rangeMin: string;
  rangeMax: string;
  accuracy: string;
  channels: string;
}

const BLANK: InstrumentValues = {
  name: '', serial: '', manufacturer: '', model: '', kind: 'gauge',
  rangeMin: '', rangeMax: '', accuracy: '', channels: '',
};

/**
 * Two calls behind one Save: POST /equipment, then POST /pressure-testing/
 * instruments. When the second fails the first has already made the unit, so
 * the dialog keeps that id and the next Save retries ONLY the second call —
 * pressing Save again must not make a second equipment row.
 */
export function AddInstrumentDialog({
  open,
  onClose,
  unit,
}: {
  open: boolean;
  onClose: () => void;
  unit: PtPressureUnit | null;
}) {
  const [v, setV] = useState<InstrumentValues>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [equipmentId, setEquipmentId] = useState<string | null>(null);
  const create = useCreatePtInstrument();

  useEffect(() => {
    if (open) {
      setV(BLANK);
      setError(null);
      setEquipmentId(null);
    }
  }, [open]);

  const set = (k: keyof InstrumentValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.name.trim()) return setError('Name is required.');
    const rangeMin = pressureFromInput(v.rangeMin);
    const rangeMax = pressureFromInput(v.rangeMax);
    if (Number.isNaN(rangeMin) || Number.isNaN(rangeMax)) return setError('Range must be a number.');
    const channels = v.kind === 'recorder' ? intFromInput(v.channels) : null;
    if (Number.isNaN(channels)) return setError('Channels must be a whole number.');

    create.mutate(
      {
        equipmentId,
        equipment: {
          name: v.name.trim(),
          type: PT_KIND_EQUIPMENT_TYPE[v.kind],
          serialNumber: v.serial.trim() || null,
          manufacturer: v.manufacturer.trim() || null,
          model: v.model.trim() || null,
        },
        specs: {
          instrument_kind: v.kind,
          range_min: rangeMin,
          range_max: rangeMax,
          accuracy_class: v.accuracy.trim() || null,
          recorder_channels: channels,
        },
      },
      {
        onSuccess: () => {
          toast.success('Instrument added');
          onClose();
        },
        onError: (err) => {
          if (err instanceof PtSpecsAttachError) {
            setEquipmentId(err.equipmentId);
            setError(
              `${ptErrorMessage(err.attachError, 'The specs were not saved.')} The equipment row was created; ` +
                'correct the specs and Save again to attach them to it.',
            );
          } else {
            setError(ptErrorMessage(err, 'The instrument could not be added.'));
          }
        },
      },
    );
  };

  // Once the equipment row exists, its fields are fixed: Save only retries the specs.
  const locked = !!equipmentId;

  return (
    <PtDialog
      open={open}
      title="Add instrument"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pt-add-instrument" className={PRIMARY} disabled={create.isPending}>
            {create.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {locked ? 'Save specs' : 'Add instrument'}
          </button>
        </>
      }
    >
      <form id="pt-add-instrument" onSubmit={submit} className="space-y-4" noValidate>
        <FormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PtField label="Name">
            <input className={PT_INPUT} value={v.name} onChange={set('name')} disabled={locked} required />
          </PtField>
          <PtField label="Serial number">
            <input className={PT_INPUT} value={v.serial} onChange={set('serial')} disabled={locked} />
          </PtField>
          <PtField label="Manufacturer">
            <input className={PT_INPUT} value={v.manufacturer} onChange={set('manufacturer')} disabled={locked} />
          </PtField>
          <PtField label="Model">
            <input className={PT_INPUT} value={v.model} onChange={set('model')} disabled={locked} />
          </PtField>
        </div>
        <SpecFields
          unit={unit}
          kind={v.kind}
          rangeMin={v.rangeMin}
          rangeMax={v.rangeMax}
          accuracy={v.accuracy}
          channels={v.channels}
          onChange={(k, value) => setV((prev) => ({ ...prev, [k]: value }))}
        />
      </form>
    </PtDialog>
  );
}

/** Kind, range, accuracy and (recorders only) channels — shared by Add and Edit. */
function SpecFields({
  unit,
  kind,
  rangeMin,
  rangeMax,
  accuracy,
  channels,
  onChange,
}: {
  unit: PtPressureUnit | null;
  kind: PtInstrumentKind;
  rangeMin: string;
  rangeMax: string;
  accuracy: string;
  channels: string;
  onChange: (key: 'kind' | 'rangeMin' | 'rangeMax' | 'accuracy' | 'channels', value: string) => void;
}) {
  const unitLabel = unit ?? '—';
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <PtField label="Kind">
        <select className={PT_INPUT} value={kind} onChange={(e) => onChange('kind', e.target.value)}>
          {PT_INSTRUMENT_KINDS.map((k) => (
            <option key={k} value={k}>
              {PT_KIND_LABEL[k]}
            </option>
          ))}
        </select>
      </PtField>
      <PtField label="Accuracy class" hint="As on the certificate, e.g. 0.25% FS">
        <input className={PT_INPUT} value={accuracy} onChange={(e) => onChange('accuracy', e.target.value)} />
      </PtField>
      <PtField label={`Range min (${unitLabel})`}>
        <input className={PT_INPUT} inputMode="decimal" value={rangeMin} onChange={(e) => onChange('rangeMin', e.target.value)} />
      </PtField>
      <PtField label={`Range max (${unitLabel})`}>
        <input className={PT_INPUT} inputMode="decimal" value={rangeMax} onChange={(e) => onChange('rangeMax', e.target.value)} />
      </PtField>
      {kind === 'recorder' && (
        <PtField label="Channels" hint="1 to 12">
          <input className={PT_INPUT} inputMode="numeric" value={channels} onChange={(e) => onChange('channels', e.target.value)} />
        </PtField>
      )}
    </div>
  );
}

// ─── Edit specs ──────────────────────────────────────────────────────────────

/**
 * PATCH sends ONLY the fields whose text changed. A range opens as the API's
 * 0.1-precision figure; sending it back unchanged would round-trip through the
 * server's psi → kPa conversion for nothing, and a field nobody touched should
 * not be a write.
 */
export function EditSpecsDialog({
  open,
  onClose,
  instrument,
  unit,
}: {
  open: boolean;
  onClose: () => void;
  instrument: PtInstrument;
  unit: PtPressureUnit | null;
}) {
  const initial = {
    kind: (instrument.specs?.instrument_kind ?? 'gauge') as PtInstrumentKind,
    rangeMin: pressureInputValue(instrument.specs?.range_min),
    rangeMax: pressureInputValue(instrument.specs?.range_max),
    accuracy: instrument.specs?.accuracy_class ?? '',
    channels: instrument.specs?.recorder_channels == null ? '' : String(instrument.specs.recorder_channels),
  };
  const [v, setV] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdatePtSpecs();

  useEffect(() => {
    if (open) {
      setV(initial);
      setError(null);
    }
    // Reset on open only; `initial` is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: PtSpecsPatch = {};
    if (v.kind !== initial.kind) body.instrument_kind = v.kind;
    if (v.rangeMin !== initial.rangeMin) body.range_min = pressureFromInput(v.rangeMin);
    if (v.rangeMax !== initial.rangeMax) body.range_max = pressureFromInput(v.rangeMax);
    if (Number.isNaN(body.range_min) || Number.isNaN(body.range_max)) return setError('Range must be a number.');
    if (v.accuracy !== initial.accuracy) body.accuracy_class = v.accuracy.trim() || null;
    // Leaving the recorder kind clears channels: the API refuses channels on any other kind.
    if (v.kind !== 'recorder' && initial.channels !== '') body.recorder_channels = null;
    else if (v.kind === 'recorder' && v.channels !== initial.channels) body.recorder_channels = intFromInput(v.channels);
    if (Number.isNaN(body.recorder_channels)) return setError('Channels must be a whole number.');
    if (Object.keys(body).length === 0) return onClose();

    update.mutate(
      { id: instrument.id, body },
      {
        onSuccess: () => {
          toast.success('Specs saved');
          onClose();
        },
        onError: (err) => setError(ptErrorMessage(err, 'The specs could not be saved.')),
      },
    );
  };

  return (
    <PtDialog
      open={open}
      title="Edit specs"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pt-edit-specs" className={PRIMARY} disabled={update.isPending}>
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </>
      }
    >
      <form id="pt-edit-specs" onSubmit={submit} className="space-y-4" noValidate>
        <FormError message={error} />
        <SpecFields
          unit={unit}
          kind={v.kind}
          rangeMin={v.rangeMin}
          rangeMax={v.rangeMax}
          accuracy={v.accuracy}
          channels={v.channels}
          onChange={(k, value) => setV((prev) => ({ ...prev, [k]: value }))}
        />
      </form>
    </PtDialog>
  );
}

// ─── Record calibration ──────────────────────────────────────────────────────

/**
 * Date, due date, certificate, performed by, notes. The date defaults to
 * `meta.today` — the TENANT's day, from the API — and cannot pass it (the API
 * refuses a future calibration). The due date has no default: it is the
 * certificate's, and a guessed one would read as the certificate's.
 */
export function RecordCalibrationDialog({
  open,
  onClose,
  instrumentId,
  today,
}: {
  open: boolean;
  onClose: () => void;
  instrumentId: string;
  today: string | null;
}) {
  const blank = { calibratedOn: today ?? '', dueOn: '', cert: '', by: '', notes: '' };
  const [v, setV] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const record = useRecordPtCalibration();

  useEffect(() => {
    if (open) {
      setV(blank);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k: keyof typeof blank) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!v.calibratedOn) return setError('Calibration date is required.');
    if (!v.dueOn) return setError('Due date is required.');
    record.mutate(
      {
        id: instrumentId,
        body: {
          calibrated_on: v.calibratedOn,
          due_on: v.dueOn,
          certificate_number: v.cert.trim() || null,
          performed_by: v.by.trim() || null,
          notes: v.notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Calibration recorded');
          onClose();
        },
        onError: (err) => setError(ptErrorMessage(err, 'The calibration could not be recorded.')),
      },
    );
  };

  return (
    <PtDialog
      open={open}
      title="Record calibration"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pt-record-calibration" className={PRIMARY} disabled={record.isPending}>
            {record.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record calibration
          </button>
        </>
      }
    >
      <form id="pt-record-calibration" onSubmit={submit} className="space-y-4" noValidate>
        <FormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PtField label="Calibrated on">
            <input type="date" className={PT_INPUT} value={v.calibratedOn} max={today ?? undefined} onChange={set('calibratedOn')} />
          </PtField>
          <PtField label="Due on" hint="The last day the calibration is valid">
            <input type="date" className={PT_INPUT} value={v.dueOn} min={v.calibratedOn || undefined} onChange={set('dueOn')} />
          </PtField>
          <PtField label="Certificate #">
            <input className={PT_INPUT} value={v.cert} maxLength={100} onChange={set('cert')} />
          </PtField>
          <PtField label="Performed by">
            <input className={PT_INPUT} value={v.by} onChange={set('by')} />
          </PtField>
        </div>
        <PtField label="Notes">
          <textarea
            className={`${PT_INPUT} h-24 py-2`}
            value={v.notes}
            onChange={set('notes')}
          />
        </PtField>
      </form>
    </PtDialog>
  );
}
